/**
 * kisiAdminService.js — İnceleme/admin tarafı "Kişi" modülü (meslek + belge kuralı,
 * personel bilgi kataloğu, kimlik doğrulama, KVKK durumu, Özel İzin).
 * Route ailesi: /Management/Kisi/*
 *
 * `personnelAdminService.js` PersonnelController'ın ESKİ 20 ucunu (kadro okuma, belge
 * onay/red, kişi oluşturma + firmaya eşleme) kapsıyor; bu dosya 2026-08-25/28'de eklenen
 * 18 ucu kapsar — bkz. docs/guides/FE-PersonnelController-kimlik-meslek-guide.md.
 * İkisi kasıtlı olarak ayrı: o servis firma kadrosu ekranlarının, bu servis kural/katalog
 * yönetimi ekranlarının servisi.
 *
 * Mock katmanı YOK (personnelAdminService.js ile aynı gerekçe): kural yazma / kimlik
 * onaylama akışları yalnızca gerçek backend'e karşı anlamlı.
 *
 * ⚠️ STAFF ZORUNLULUĞU (rehber §2). Dokuz uç "SAKA personeli" ister ve bunu
 * `user_to_user_group` satırından anlar. Grup satırı OLMAYAN kullanıcı da FİRMA sayılır —
 * yani `X-user-Id: 1` (bypass varsayılanı) bu uçlarda reddedilir. Yediisi **403**, ikisi
 * (`PATCH /{personId}/kimlik-dogrulama`, `PUT /{personId}/bilgi`) **400** döner; bu yüzden
 * sadece status koduna değil, mesaja da bakıyoruz (bkz. isStaffRefusal). Yerel testte
 * gerçek bir personel id'si gönderin: 18 / 519 / 508.
 *
 * ⚠️ `GenericResultJson` başarısızlığı HTTP 200 + `result:false` olarak gelir (bkz.
 * FE-README-guide.md) — `unwrapEnvelope` bunu hataya çevirir.
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import { getPersonelRosterAdmin } from './personnelAdminService';
import i18n from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;
const DOC_TEMPLATE_BASE = `${JAVA_BACKEND_API_URL}/document-templates`;

// personnelAdminService.js'teki aynı sabitlerin yeniden dışa vurumu — Kişi modülünün
// sayfaları da aynı test firma/fuar ikilisini varsayılan alıyor.
export { DEFAULT_FIRMA_ID, DEFAULT_FUAR_ID } from './personnelAdminService';

// ─── Sabitler (backend model/data/constants/, bkz. FE-README-guide.md "Constants") ────

/** `PersonKimlikDogrulamaConstant` */
export const KIMLIK_DURUM = {
  0: { get label() { return i18n.t("services:kisiAdminService.dogrulanmadi"); }, color: 'secondary' },
  1: { get label() { return i18n.t("services:kisiAdminService.onaylandi"); }, color: 'success' },
  2: { get label() { return i18n.t("services:kisiAdminService.reddedildi"); }, color: 'danger' },
  // #664 (backend #663): pasaport belgesi masada onaylandı, kişi alanda görülecek. Doğrulanmış SAYILMAZ (kapı yine 1 ister).
  3: { get label() { return i18n.t("services:kisiAdminService.alandaDogrulanacak"); }, color: 'info' },
};

/**
 * `PersonKvkkStatusConstant`. ⚠️ Giriş kuralını YALNIZCA 3 karşılar — 1 ve 2 saklanır
 * ama etkisizdir (rehber §7: 11.344 kişide 1 var ve bilerek dikkate alınmıyor).
 */
export const KVKK_STATUS = {
  0: { get label() { return i18n.t("services:kisiAdminService.yok"); }, color: 'secondary', satisfies: false },
  1: { get label() { return i18n.t("services:kisiAdminService.onaylandiEtkisiz"); }, color: 'warning', satisfies: false },
  2: { get label() { return i18n.t("services:kisiAdminService.reddedildi"); }, color: 'danger', satisfies: false },
  3: { get label() { return i18n.t("services:kisiAdminService.islakImzaliAsilTeslimAlindi"); }, color: 'success', satisfies: true },
};

/** `PersonBilgiVeriTipiConstant` */
export const VERI_TIPLERI = [
  { value: 'METIN', get label() { return i18n.t("services:kisiAdminService.metin"); }, get hint() { return i18n.t("services:kisiAdminService.serbestMetinEnFazla1000"); } },
  { value: 'SAYI', get label() { return i18n.t("services:kisiAdminService.sayi"); }, get hint() { return i18n.t("services:kisiAdminService.ondalikKabulEder35"); } },
  { value: 'TARIH', get label() { return i18n.t("services:kisiAdminService.tarih"); }, get hint() { return i18n.t("services:kisiAdminService.yalnizcaIsoYyyyAaGg"); } },
  { value: 'EVET_HAYIR', get label() { return i18n.t("services:kisiAdminService.evetHayir"); }, get hint() { return i18n.t("services:kisiAdminService.evetHayir10True"); } },
  { value: 'SECIM', get label() { return i18n.t("services:kisiAdminService.secimListesi"); }, get hint() { return i18n.t("services:kisiAdminService.seceneklerIceremez"); } },
];

/** Meslek belge kuralı hangi seviyeden çözüldü — `kaynak` (rehber §5, beş kademe). */
export const KURAL_KAYNAK = {
  FUAR: { get label() { return i18n.t("services:kisiAdminService.buFuarBuMeslek"); }, color: 'primary' },
  FUAR_GENEL: { get label() { return i18n.t("services:kisiAdminService.buFuarTumMeslekler"); }, color: 'info' },
  GLOBAL: { get label() { return i18n.t("services:kisiAdminService.tumFuarlarBuMeslek"); }, color: 'secondary' },
  GLOBAL_GENEL: { get label() { return i18n.t("services:kisiAdminService.tumFuarlarTumMeslekler"); }, color: 'secondary' },
  VARSAYILAN: { get label() { return i18n.t("services:kisiAdminService.varsayilanKuralYazilmamis"); }, color: 'dark' },
  // Yazılmış bir kural DEĞİL, hesaplanmış (backend #125): firma bu fuarda yalnızca Katılımcı /
  // Katılımcı Destek Personeli ise çalışanı için SGK yeter (meslek ne olursa olsun). Kural
  // düzenleyicisinin kademelerinden biri değil; yalnızca kişi satırlarının `belgeKurali.kaynak`ında çıkar.
  KATILIMCI_CALISANI: { get label() { return i18n.t("services:kisiAdminService.katilimciFirmaCalisani"); }, color: 'warning' },
  // #372 (backend #371): kişi bu fuarda 2+ meslek taşıyor — kural, mesleklerin kurallarının BİRLEŞİMİ (her biri kendi
  // kademesinden çözülür; hesaplanmış, yazılmış bir kural değil).
  COKLU_MESLEK: { get label() { return i18n.t("services:kisiAdminService.birdenCokMeslekBirlesimi"); }, color: 'primary' },
};

/**
 * `SinglePermitConstant` — tipler yalnızca İNİSİYATİFİ / dayanağı kaydeder, hiçbiri diğerinden üstün değil
 * (rehber §7: "Do not build a UI implying 3 outranks 1").
 * #474/#475: YENİ izinde yalnızca 3 / 2 / 5 verilebilir (`verilebilir`); 1 ve 4 eski satırlarda okunur, yeni izinde backend 400 döner.
 * WAS (until #475): 1 "Firma talebi üzerine" (etiket yanlıştı — backend 1'i "Nehir İSGM insiyatifinde" diye saklıyordu), 2 "Fuar organizatörü talebi
 * üzerine", 3 "SAKA inisiyatifi", 4 "Diğer" — hepsi seçilebiliyordu.
 */
export const OZEL_IZIN_TIPLERI = [
  { value: 3, verilebilir: true, get label() { return i18n.t("services:kisiAdminService.sakaGrupInisiyatifi"); } },
  { value: 2, verilebilir: true, get label() { return i18n.t("services:kisiAdminService.organizatorTalebiUzerine"); } },
  { value: 5, verilebilir: true, get label() { return i18n.t("services:kisiAdminService.belgeleriAlinmistir"); } },
  { value: 1, verilebilir: false, get label() { return i18n.t("services:kisiAdminService.nehirIsgmInisiyatifi"); } },
  { value: 4, verilebilir: false, get label() { return i18n.t("services:kisiAdminService.diger"); } },
];

/** Yeni bir izin verirken seçilebilen tipler (select'in seçenekleri). */
export const OZEL_IZIN_VERILEBILIR_TIPLER = OZEL_IZIN_TIPLERI.filter((t) => t.verilebilir);

/** Yeni izinde başlangıç tipi: Saka Grup inisiyatifi. */
export const OZEL_IZIN_VARSAYILAN_TIP = 3;

/**
 * #474: kimliği doğrulanmamış (`kimlikDogrulama.durum` != 1) kişiye Özel İzin verilmez — backend 409 + `data.kod`
 * `KIMLIK_DOGRULANMAMIS` döner (geri almak her zaman serbest). `grantSinglePermit` hatası bu mu?
 */
export function isKimlikDogrulanmamisHatasi(error) {
  return error?.status === 409 && error?.data?.kod === 'KIMLIK_DOGRULANMAMIS';
}

/** KVKK kişi belgesi — meslek kuralına EKLENEMEZ (backend 400 döner). */
export const KVKK_FILE_TYPE_ID = 56;

/** `file_type` ağacında kişi belgelerinin kökü (GET /document-templates/file-types?parentId=1). */
export const KISI_FILE_TYPE_PARENT_ID = 1;

// ─── Kişi belge anahtarları (documents{} sözlüğü) ─────────────────────────────────────

/**
 * `FirmaPersonelDTO.documents` sözleşmedeki BEŞ anahtarı her zaman taşır; meslek kuralı
 * fazladan anahtar ekleyebilir (`yuksekteCalisma`, `asiKarti`, `kimlikFotografi`,
 * `fileType{id}`). Bilinmeyen anahtar geldiğinde etiket olarak anahtarın kendisi kullanılır.
 */
export const KISI_BELGE_META = {
  kvkk: { label: 'KVKK' },
  sgk: { label: 'SGK' },
  isg: { get label() { return i18n.t("services:kisiAdminService.isgEgitimi"); } },
  saglik: { get label() { return i18n.t("services:kisiAdminService.saglikOnayi"); } },
  meslekiEgitim: { get label() { return i18n.t("services:kisiAdminService.meslekiEgitim"); } },
  yuksekteCalisma: { get label() { return i18n.t("services:kisiAdminService.yuksekteCalisma"); } },
  asiKarti: { get label() { return i18n.t("services:kisiAdminService.asiKarti"); } },
  kimlikFotografi: { get label() { return i18n.t("services:kisiAdminService.kimlikFotografi"); } },
};

export const kisiBelgeLabel = (key) => KISI_BELGE_META[key]?.label ?? key;

/**
 * `file_type` 90 "Kimlik Fotoğrafı" (KisiDocumentConstant.KIMLIK_FOTOGRAFI) — kimlik kartı ya da
 * pasaportun KENDİSİ, normal bir kişi belgesi gibi yüklenir. ⚠️ Üç şey karıştırılır, bu yalnızca
 * üçüncüsü: `person_logo` (kartın vesikalığı), `person_kimlik_dogrulama_file_id` (#469'dan beri EMEKLİ:
 * eski dayanak işaretçisi, backend artık yazmıyor ve okumuyor) ve bu. Yükleme alanını ilk ikisine bağlamayın.
 */
export const KIMLIK_FOTOGRAFI_FILE_TYPE_ID = 90;

/** Backend `ALLOWED_DOCUMENT_EXTENSIONS` / `MAX_UPLOAD_BYTES` — istemci tarafı ön kontrol. */
export const KIMLIK_BELGESI_UZANTILAR = ['pdf', 'jpg', 'jpeg', 'png'];
export const KIMLIK_BELGESI_MAX_MB = 20;

// ─── Kimlik doğrulamanın dayanağı: belge türü (#469 / #470) ──────────────────────────

/**
 * `person_kimlik_dogrulama_belge_turu` — kimliğin NEYE bakılarak doğrulandığı. Karara bağlıdır (kişi düzeyinde, Onayla /
 * Reddet ile birlikte kaydolur), yüklenen dosyaya değil; dosya ise tek ortak "Kimlik belgesi" alanıdır (file_type 90).
 * `filtre` Kişi Listesi'nin `kimlikTipi` süzgeç değeri. `dosyaGerekir`: backend bu dört türü kişide tip 90 dosya yokken
 * 400 ile reddeder; `aciklamaGerekir`: Özel durum açıklamasız reddedilir. Elden görüldü ikisini de istemez.
 * Sabit liste, katalog tablosu yok; etiketler tr / en anahtarları.
 * WAS (until #470): kararın dayanağı kişinin başka bir belgesine işaret eden `fileId` idi (dayanak seçici,
 * `dayanakSecenekleriFromDocuments`, MOCK_DAYANAK_FILES); backend o işaretçiyi artık yok sayıyor.
 */
export const KIMLIK_BELGE_TURLERI = [
  { value: 'ELDEN_GORULDU', filtre: 'elden_goruldu', dosyaGerekir: false, aciklamaGerekir: false, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.eldenGoruldu"); } },
  { value: 'TC_KIMLIK', filtre: 'kimlik', dosyaGerekir: true, aciklamaGerekir: false, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.kimlik"); } },
  { value: 'EHLIYET', filtre: 'ehliyet', dosyaGerekir: true, aciklamaGerekir: false, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.ehliyet"); } },
  { value: 'PASAPORT', filtre: 'pasaport', dosyaGerekir: true, aciklamaGerekir: false, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.pasaport"); } },
  { value: 'DIGER', filtre: 'diger', dosyaGerekir: true, aciklamaGerekir: false, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.diger"); } },
  { value: 'OZEL_DURUM', filtre: 'ozel_durum', dosyaGerekir: false, aciklamaGerekir: true, get label() { return i18n.t("services:kisiAdminService.kimlikBelgeTuru.ozelDurum"); } },
];

/** Backend girişte "KIMLIK"i de kabul eder ama TC_KIMLIK olarak saklar; yine de ikisini aynı sayarız. */
export function kimlikBelgeTuruNormalize(deger) {
  if (!deger) return null;
  const v = String(deger).toUpperCase();
  return v === 'KIMLIK' ? 'TC_KIMLIK' : v;
}

/** Saklı türün gösterim adı; tanınmayan değer olduğu gibi döner, boşsa null. */
export function kimlikBelgeTuruLabel(deger) {
  const v = kimlikBelgeTuruNormalize(deger);
  if (!v) return null;
  return KIMLIK_BELGE_TURLERI.find((x) => x.value === v)?.label ?? String(deger);
}

// ─── Ortak yardımcılar ───────────────────────────────────────────────────────────────

// Backend 400/500'ü bazen düz metin, bazen {message,...} zarfı döner (bkz. rehber
// "Error bodies are inconsistent") — diğer servislerdeki aynı yardımcının kopyası.
function parseError(error) {
  const message =
    typeof error.response?.data === 'string'
      ? error.response.data
      : error.response?.data?.message ?? error.message ?? i18n.t("services:kisiAdminService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  // Zarfın `data`sı hatada da anlamlı olabilir: POST /meslek bir ad çakışmasında 409 ile
  // çakışan mesleği (`data.kaldirildi` dahil) bu alanda döner.
  wrapped.data = error.response?.data?.data ?? null;
  throw wrapped;
}

/**
 * `{message, result, data}` zarfını açar. ⚠️ `result:false` HTTP 200 ile gelir — bu
 * yüzden axios hata fırlatmaz ve kontrolü burada yapmak zorundayız (bkz. rehber).
 */
function unwrapEnvelope(response) {
  const body = response.data;
  if (body && typeof body === 'object' && 'result' in body) {
    if (body.result === false) {
      const err = new Error(body.message || i18n.t("services:kisiAdminService.islemBasarisiz"));
      err.status = response.status;
      throw err;
    }
    return body.data;
  }
  return body;
}

/**
 * Bir hatanın "SAKA personeli değilsin" reddi olup olmadığı. Yedi uç 403, iki uç 400
 * döndüğü için tek başına status koduna bakmak yetmiyor (rehber §2).
 */
export function isStaffRefusal(error) {
  if (error?.status === 403) return true;
  const msg = String(error?.message || '');
  return /SAKA personeli/i.test(msg);
}

// ─── Belge tipi kataloğu ─────────────────────────────────────────────────────────────

/**
 * Kişi belgelerinin kataloğu — meslek belge kuralı editöründeki çoklu seçim kutusu.
 * `parentId=1` "Kişi" kökünün 11 çocuğunu döner. Zarf YOK, düz dizi döner.
 * ⚠️ Anahtar sırası satır satır değişir (HashMap) — anahtarla eriş, sırayla değil.
 * GET /api/v1/document-templates/file-types?parentId=1
 */
export async function getKisiFileTypes() {
  try {
    const response = await axios.get(`${DOC_TEMPLATE_BASE}/file-types`, {
      params: { parentId: KISI_FILE_TYPE_PARENT_ID },
      headers: getAuthHeader(),
    });
    return (response.data || [])
      .map((row) => ({
        fileTypeId: row.fileTypeId,
        fileTypeName: row.fileTypeName,
        extra: row.extra ?? null,
        isImage: Number(row.isImage) === 1,
      }))
      .sort((a, b) => a.fileTypeId - b.fileTypeId);
  } catch (error) {
    parseError(error);
  }
}

// ─── Meslek + belge kuralı (rehber §5) ───────────────────────────────────────────────

/**
 * Meslek listesi — her satırda kişi sayısı ve ÇÖZÜLMÜŞ belge kuralı.
 * İlk satır her zaman `meslekId: 0` "Tüm meslekler (varsayılan)"; oradaki `kisiSayisi`
 * mesleği KAYITLI OLMAYAN kişilerin sayısıdır (o varsayılanın gerçekten karar verdiği
 * kişiler), diğer satırlarda mesleği o olan kişi sayısıdır.
 *
 * ⚠️ Varsayılan olarak yalnızca SUNUMDA olan meslekler gelir — her meslek açılır listesi
 * bunu istemeli. `kaldirilanlar: true` kaldırılmışları da ekler (`kaldirildi: true`,
 * `kaldirilmaTarihi`, `kaldiranUserId`); iki kullanımı var: yönetim sayfasının "Kaldırılan
 * Meslekler" bölümü ve bir kişinin kaldırılmış mevcut mesleğinin adını çözmek. Kaldırılmış
 * satır da `kisiSayisi` ve `belgeKurali` taşır.
 * GET /api/v1/personnel/meslek?fuarId=&kaldirilanlar=
 */
export async function getMeslekList(fuarId = 0, { kaldirilanlar = false } = {}) {
  try {
    const response = await axios.get(`${BASE}/meslek`, {
      params: { fuarId: Number(fuarId) || 0, ...(kaldirilanlar ? { kaldirilanlar: true } : {}) },
      headers: getAuthHeader(),
    });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Tek bir mesleğin belge kuralı, ÜÇ SEVİYE BİRDEN (editör için):
 * `{ etkin, fuarKurali, globalKural }`. `meslekId: 0` = "bu fuar herkesten ne istiyor".
 * ⚠️ `etkin.fileTypeIds: []` + `kaynak: "VARSAYILAN"` ile `kaynak: "GLOBAL"` ZIT anlamda:
 * ilki "kimse karar vermedi → varsayılan dörtlü", ikincisi "bu meslek hiçbir belge
 * istemiyor". Her zaman kaynağı da göster.
 * GET /api/v1/personnel/meslek/{meslekId}/belge-kural?fuarId=
 */
// #430: `uyruk` (0 hepsi, 1 T.C. kişi, 2 yabancı kişi; varsayılan 0 = çağrının hep anlamı) uyruk DÜZEYİNİ seçer: { uyruk, etkin, fuarKurali, globalKural } o düzeyin.
export async function getMeslekBelgeKural(meslekId, fuarId = 0, uyruk = 0) {
  try {
    const response = await axios.get(`${BASE}/meslek/${meslekId}/belge-kural`, {
      params: { fuarId: Number(fuarId) || 0, uyruk: Number(uyruk) || 0 },
      headers: getAuthHeader(),
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Belge kuralını YAZAR — staff. `fileTypeIds` TÜM kümedir (merge değil, replace).
 * `[]` geçerlidir ve "hiç kişisel belge istemiyor" demektir; kuralı bir üst kademeye
 * BIRAKMAK için `deleteMeslekBelgeKural` kullanın.
 * ⚠️ `fuarId: 0` = tüm fuarlar; bir fuar id'si o fuarda global kümenin YERİNE geçer ve
 * fuar boyutu meslek boyutundan ÜSTÜNDÜR (rehber §5) — "fuar 298'de herkes X" yazmak o
 * fuardaki global nakliyeci kuralını yutar.
 * ⚠️ KVKK (56) listeye eklenemez → 400.
 * PUT /api/v1/personnel/meslek/{meslekId}/belge-kural
 */
// #430: `uyruk` kuralın hangi uyruk düzeyi için olduğunu söyler (0 hepsi, 1 T.C. kişi, 2 yabancı kişi).
export async function saveMeslekBelgeKural(meslekId, { fuarId = 0, uyruk = 0, fileTypeIds = [], aciklama = '' } = {}) {
  try {
    const response = await axios.put(
      `${BASE}/meslek/${meslekId}/belge-kural`,
      {
        fuarId: Number(fuarId) || 0,
        uyruk: Number(uyruk) || 0,
        fileTypeIds: fileTypeIds.map(Number),
        aciklama: aciklama || null,
      },
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response); // yazıldığı KAPSAMDAKİ kural, çözülmüş cascade değil
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kuralı o KESİN kapsamdan siler, meslek bir üst kademeye düşer — `[]` yazmakla aynı şey
 * DEĞİL. `data` düşülen (artık yürürlükte olan) kuraldır, ikinci bir çağrı gerekmez.
 * 404 = o kapsamda zaten kural yok.
 * DELETE /api/v1/personnel/meslek/{meslekId}/belge-kural?fuarId=
 */
// #430: `uyruk` hangi düzeyin kuralının gideceğini söyler (varsayılan 0).
export async function deleteMeslekBelgeKural(meslekId, fuarId = 0, uyruk = 0) {
  try {
    const response = await axios.delete(`${BASE}/meslek/${meslekId}/belge-kural`, {
      params: { fuarId: Number(fuarId) || 0, uyruk: Number(uyruk) || 0 },
      headers: getAuthHeader(),
    });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/** #430: belge kuralının uyruk düzeyleri — backend UyrukConstant: 0 hepsi, 1 T.C. kişi, 2 yabancı kişi. */
export const UYRUK_DUZEYI = { HEPSI: 0, YERLI: 1, YABANCI: 2 };

/**
 * #430: kişi uyruk alanının GÖZDEN GEÇİRMESİ (salt okunur, staff + page.kisi): { uyrukEsasi, yerli, yabanci, bilinmeyen, tutarli, siniflar: [{ sinif, adet }] }.
 * Sınıflar: YABANCI_TC_NUMARALI, YERLI_TC_NUMARASIZ, UYRUK_YOK. "Kişinin uyruğu esas alınsın" ayarı AÇILMADAN önce bakılır.
 * GET /api/v1/personnel/uyruk-kontrol
 */
export async function getUyrukKontrol() {
  try {
    const response = await axios.get(`${BASE}/uyruk-kontrol`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * #430: bir sınıftaki kişilerin bir sayfası (en yeni kart önce, size ≤ 100): { sinif, sayfa, boyut, toplam, kayitlar: [{ personId, ad, soyad, kimlikNoMaskeli, pasaportNo, uyrukId }] }.
 * GET /api/v1/personnel/uyruk-kontrol/{sinif}?page=&size=
 */
export async function getUyrukKontrolListe(sinif, page = 0, size = 25) {
  try {
    const response = await axios.get(`${BASE}/uyruk-kontrol/${sinif}`, { params: { page, size }, headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Bir kişinin BU FUARDAKİ mesleğini kaydeder (`person_firma_to_fuar.meslek_id`) — staff.
 * `meslekId: 0` mesleği TEMİZLER. Kişi o fuarda birden çok firmada çalışıyorsa `firmaId`
 * zorunlu (aksi halde backend firmaları sayarak 400 döner).
 * `data` artık geçerli olan kuraldır — mesleği seçen, işçinin hangi belgeleri üretmek
 * zorunda olduğunu da seçmiş olur, bu yüzden uç staff-only.
 *
 * #372 (backend #371): bir işe alım BİRDEN ÇOK meslek taşır — `meslekIds` o işe alımın TÜM kümesini DEĞİŞTİRİR
 * (tek transaction), `[]` temizler. `meslekId` yalnızca eski çağıranlar için: tek elemanlı küme sayılır.
 * `data.kaynak` COKLU_MESLEK ise kişi o fuarda 2+ meslek taşıyor (başka firmanınkiler dahil).
 * PATCH /api/v1/personnel/{personId}/meslek
 */
export async function setPersonMeslek(personId, { fuarId, meslekId = 0, meslekIds = null, firmaId = null } = {}) {
  try {
    const body = { fuarId: Number(fuarId) };
    if (Array.isArray(meslekIds)) body.meslekIds = meslekIds.map(Number).filter((id) => id > 0);
    else body.meslekId = Number(meslekId) || 0;
    if (firmaId) body.firmaId = Number(firmaId);
    const response = await axios.patch(`${BASE}/${personId}/meslek`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

// ─── Kişinin kendi meslekleri (#466, backend #464: `person_meslek`) ──────────────────────
// Meslek KİŞİNİNDİR: fuardan ve firmadan bağımsız. SAKA personeli her şeyi yapar; bir firma kendi çalışanına ekler
// (hemen görünür, kartın o meslekle basılması kuralı hafifse personel onayına bağlıdır) ve yalnızca kendi eklediği,
// onaylı sertifikası olmayan mesleği kaldırır. Yanıtlar `GenericResultJson` zarfıdır (GET düz dizi dönebilir).

const meslekListesiOf = (govde) => {
  const veri = govde && typeof govde === 'object' && !Array.isArray(govde) && 'data' in govde ? govde.data : govde;
  return Array.isArray(veri) ? veri : [];
};

/**
 * Kişinin tuttuğu meslekler: `[{ meslekId, meslekAdi, kategoriId, kategoriAdi, kaynak SAKA|FIRMA|KISI, buFirmaEkledi,
 * ekleyenFirmaId, ekleyenFirmaAdi, eklenmeTarihi, onayli, onayTarihi, meslekKaldirildi }]`, en eskisi önce. Başka firmanın
 * adı firma girişinde null gelir.
 * GET /api/v1/personnel/{personId}/meslekler
 */
export async function getKisiMeslekleri(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/meslekler`, { headers: getAuthHeader() });
    return meslekListesiOf(response.data);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişiye meslek ekler. Personel: SAKA olarak, onaylı. Firma: kendi çalışanına, FIRMA olarak, onaysız. 201 + güncel liste;
 * 400 kaldırılmış meslek · 404 bilinmeyen · 409 kişide zaten var · 403 çalışanı değil / PERSONEL yazma yetkisi yok.
 * POST /api/v1/personnel/{personId}/meslekler  { meslekId }
 */
export async function addKisiMeslegi(personId, meslekId) {
  try {
    const response = await axios.post(`${BASE}/${personId}/meslekler`, { meslekId: Number(meslekId) }, { headers: getAuthHeader() });
    unwrapEnvelope(response);
    return meslekListesiOf(response.data);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Mesleği kişiden kaldırır (yumuşak). Personel her şeyi; firma yalnızca kendi eklediğini ve onaylı sertifikası olmayanı
 * (403 `kisi.meslek_firma_eklemedi` / `kisi.meslek_onayli_belge_var`). 404 kişide yok.
 * DELETE /api/v1/personnel/{personId}/meslekler/{meslekId}
 */
export async function removeKisiMeslegi(personId, meslekId) {
  try {
    const response = await axios.delete(`${BASE}/${personId}/meslekler/${meslekId}`, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Personel, bir firmanın eklediği mesleği onaylar (kural "Çalışan" setinden hafifse kart ancak bundan sonra o meslekle
 * basılır). 404 kişide yok · 409 zaten onaylı · firma girişi 403.
 * POST /api/v1/personnel/{personId}/meslekler/{meslekId}/onayla
 */
export async function onaylaKisiMeslegi(personId, meslekId) {
  try {
    const response = await axios.post(`${BASE}/${personId}/meslekler/${meslekId}/onayla`, null, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Yeni meslek oluşturur — staff. Kural yazılmaz; meslek, biri yazılana kadar cascade'den
 * çözülür. `ad` en fazla 255, `aciklama` en fazla 500 karakter.
 * ⚠️ Ad zaten kullanılıyorsa **409** (büyük/küçük harf ayırmadan: `nakliye` = `NAKLİYE`) ve
 * `error.data` o meslektir. `error.data.kaldirildi === true` ise ad KALDIRILMIŞ bir mesleğe
 * aittir: kopya açmak yerine `geriAlMeslek(error.data.meslekId)` önerin — kopya, kişileri
 * ve kuralı ikiye bölerdi.
 * POST /api/v1/personnel/meslek
 */
export async function createMeslek({ ad, aciklama = '' } = {}) {
  try {
    const response = await axios.post(
      `${BASE}/meslek`,
      { ad: String(ad || '').trim(), aciklama: aciklama || null },
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Mesleği KATALOGDAN kaldırır — staff, gövde yok. Silme DEĞİL: mesleği taşıyan kişiler onu
 * korur, kuralı hâlâ geçerli ve düzenlenebilir kalır; yalnızca açılır listelerde sunulmaz ve
 * yeni atanamaz. `belge-kural` DELETE'iyle (bir KURALI bir kademe yukarı bırakmak) karıştırmayın.
 * `data`: `{ meslek, kisiSayisi, kartMeslegiKisiSayisi }` — `kisiSayisi` bütün fuarlardaki
 * işe alımlar, `kartMeslegiKisiSayisi` kişi kartında (yedek) bunu taşıyanlar.
 * 400 meslekId 0 · 404 bilinmeyen · 409 zaten kaldırılmış.
 * POST /api/v1/personnel/meslek/{meslekId}/kaldir
 */
export async function kaldirMeslek(meslekId) {
  try {
    const response = await axios.post(`${BASE}/meslek/${meslekId}/kaldir`, null, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kaldırılmış mesleği tekrar sunuma alır — kimin ne zaman kaldırdığı temizlenir. Gövde yok,
 * `data` şekli `kaldirMeslek` ile aynı. 404 bilinmeyen · 409 zaten kaldırılmamış.
 * POST /api/v1/personnel/meslek/{meslekId}/geri-al
 */
export async function geriAlMeslek(meslekId) {
  try {
    const response = await axios.post(`${BASE}/meslek/${meslekId}/geri-al`, null, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

// ─── Personel bilgi kataloğu (rehber §6) ─────────────────────────────────────────────

/**
 * Bilgi kategorileri. `onlyActive=false` emekliye ayrılanları da getirir — yönetim
 * ekranında bunu gönderin. `kullanimSayisi` her iki GET'te de var.
 * Sıra: `siralama ASC, ad ASC` — geldiği sırada render edin.
 * GET /api/v1/personnel/bilgi-tipi?onlyActive=
 */
export async function getBilgiTipleri({ onlyActive = true } = {}) {
  try {
    const response = await axios.get(`${BASE}/bilgi-tipi`, {
      params: { onlyActive },
      headers: getAuthHeader(),
    });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/** POST /api/v1/personnel/bilgi-tipi — staff. 201, `data` = saklanan tanım. */
export async function createBilgiTipi(payload) {
  try {
    const response = await axios.post(`${BASE}/bilgi-tipi`, normalizeBilgiTipiPayload(payload), {
      headers: getAuthHeader(),
    });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * PUT /api/v1/personnel/bilgi-tipi/{id} — staff. `kod` DIŞINDA tam replace.
 * ⚠️ `kod` DEĞİŞTİRİLEMEZ (değerler/dışa aktarımlar ona asılı) — farklı bir `kod`
 * göndermek 400; hiç göndermemek mevcut kodu korur, bu yüzden hiç göndermiyoruz.
 * ⚠️ `veriTipi` ve `fuarBazli`, tek bir değer bile girilmişse değiştirilemez → 400.
 */
export async function updateBilgiTipi(id, payload) {
  try {
    const body = normalizeBilgiTipiPayload(payload);
    delete body.kod;
    const response = await axios.put(`${BASE}/bilgi-tipi/${id}`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * PATCH /api/v1/personnel/bilgi-tipi/{id}/aktif — staff. SİLMEK YERİNE EMEKLİYE AYIR:
 * form artık sormaz ama toplanmış her değer durur ve o değere sahip kişilerde dönmeye
 * devam eder. Bir soru önemsizleşebilir, ama cevaplar yok olmaz.
 */
export async function setBilgiTipiAktif(id, aktif) {
  try {
    const response = await axios.patch(
      `${BASE}/bilgi-tipi/${id}/aktif`,
      { aktif: Boolean(aktif) },
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * DELETE /api/v1/personnel/bilgi-tipi/{id} — staff. Yalnızca `kullanimSayisi === 0` iken;
 * aksi halde **409**. ⚠️ Bilinmeyen id de 409 döner (aynı kod!) — "böyle bir kategori yok"
 * ile "hâlâ kullanımda"yı ayırt etmek için MESAJA bakın, koda değil.
 */
export async function deleteBilgiTipi(id) {
  try {
    const response = await axios.delete(`${BASE}/bilgi-tipi/${id}`, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

function normalizeBilgiTipiPayload({
  kod, ad, aciklama = '', veriTipi = 'METIN', secenekler = [],
  zorunlu = false, firmaGirebilir = true, fuarBazli = false, siralama = 0, aktif = true,
} = {}) {
  const body = {
    kod: String(kod || '').trim().toLowerCase(),
    ad: String(ad || '').trim(),
    aciklama: aciklama || null,
    veriTipi,
    zorunlu: Boolean(zorunlu),
    firmaGirebilir: Boolean(firmaGirebilir),
    fuarBazli: Boolean(fuarBazli),
    siralama: Number(siralama) || 0,
    aktif: Boolean(aktif),
  };
  // `secenekler` yalnızca SECIM için anlamlı; diğer tiplerde göndermiyoruz ki
  // backend'de gereksiz bir ";" doğrulamasına takılmasın.
  if (veriTipi === 'SECIM') body.secenekler = (secenekler || []).map((s) => String(s).trim()).filter(Boolean);
  return body;
}

/**
 * Bir kişinin ekstra alanları — her satır TANIMIYLA BİRLİKTE gelir, form tek response'tan
 * render edilir. Aktif her kategori + bu kişinin değeri olan emekli kategoriler döner.
 * `deger: null` = kayıt yok (boş string durumu yoktur).
 * GET /api/v1/personnel/{personId}/bilgi?fuarId=
 */
export async function getPersonBilgi(personId, fuarId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/bilgi`, {
      params: { fuarId: Number(fuarId) || 0 },
      headers: getAuthHeader(),
    });
    return response.data || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Değerleri `kod` ile yazar. HEPSİ YA DA HİÇBİRİ: tek bir hatalı alan bütün paketi
 * reddeder. `null`/`""` o alanı TEMİZLER; gövdede geçmeyen kategoriye dokunulmaz.
 * `fuarId` yalnızca `fuarBazli` kategorilerce okunur.
 * ⚠️ `TARIH` yalnızca ISO (yyyy-AA-gg) kabul eder — "12.08.2026" 400.
 * `data` kişinin TAMAMEN tazelenmiş listesidir (GET ile aynı şekil), ikinci çağrı gerekmez.
 * PUT /api/v1/personnel/{personId}/bilgi
 */
export async function savePersonBilgi(personId, { fuarId = 0, degerler = {} } = {}) {
  try {
    const response = await axios.put(
      `${BASE}/${personId}/bilgi`,
      { fuarId: Number(fuarId) || 0, degerler },
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

// ─── Kimlik doğrulama (rehber §4) ────────────────────────────────────────────────────

/**
 * "Bir insan, bu işçinin kartındaki TC kimlik numarasının sahibi olduğunu doğruladı mı?"
 * Kişi seviyesinde ve KALICI — kimlik fuardan fuara değişmez.
 * 200 her zaman (kişi yoksa 404). `dogrulandi` sadece `durum === 1`.
 * #469: `belgeTuru` (KIMLIK_BELGE_TURLERI.value ya da null) ve `kimlikBelgesi` (kişinin en yeni tip 90 dosyası,
 * `documents.kimlikFotografi` şeklinde: fileId, status, expiryDate, yükleyen…) döner; `fileId` artık dönmez.
 * GET /api/v1/personnel/{personId}/kimlik-dogrulama
 */
export async function getKimlikDogrulama(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/kimlik-dogrulama`, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kimlik doğrulamayı yazar — staff, ama reddi **400** ile gelir (kontrol controller
 * guard'ında değil servis içinde), bkz. isStaffRefusal.
 * `durum` 0/1/2; `durum: 2` için `aciklama` ZORUNLU (maks. 500 karakter).
 * #470: `belgeTuru` (KIMLIK_BELGE_TURLERI.value) kimliğin neye bakılarak doğrulandığıdır, isteğe bağlı; GÖNDERİLMEZSE
 * backend kayıtlı türü SİLER (Sıfırla bu yüzden türsüz gider). Dört belge türü kişide tip 90 dosya yokken, Özel durum
 * açıklamasız ve Pasaport onayı pasaport numarasızken 400 döner (mesajı olduğu gibi gösterin).
 * WAS (until #470): `fileId` (dayanak belge işaretçisi); backend artık yok sayıyor.
 * Doğrulayan oturumdan alınır ve 0'a SIFIRLAMA dahil HER yazımda damgalanır.
 * #664 (backend #663): `yuzYuze: true` = görevli kişiyi yüz yüze gördü. Pasaport ONAYI bunu ister (yoksa 400
 * PERSONEL_PASAPORT_YUZ_YUZE_GEREKLI); masadaki belge kontrolü için setPasaportBelgesi. Durum 3 bu uçtan yazılamaz.
 * PATCH /api/v1/personnel/{personId}/kimlik-dogrulama
 */
export async function setKimlikDogrulama(personId, { durum, aciklama = '', belgeTuru = null, yuzYuze = false } = {}) {
  try {
    const body = { durum: Number(durum) };
    if (aciklama) body.aciklama = aciklama;
    if (belgeTuru) body.belgeTuru = kimlikBelgeTuruNormalize(belgeTuru);
    if (yuzYuze) body.yuzYuze = true;
    const response = await axios.patch(`${BASE}/${personId}/kimlik-dogrulama`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * #664 (backend #663): yüklenmiş pasaport belgesinin MASADA kontrolü — kişi henüz görülmedi.
 * `durum` 1 = belgeyi onayla (dosya `gecerlilikTarihi` ile onaylanır, ya da dosyanın bugün/sonrası kendi tarihiyle; kimlik
 * durumu 3 "Belge onaylı, alanda doğrulanacak" olur, doğrulanmış SAYILMAZ); `durum` 2 = belgeyi reddet (`aciklama` zorunlu,
 * 3 ise 0'a döner, "Kimlik Reddedildi" OLMAZ). Pasaport numarası gerekir. Kişi zaten doğrulanmışsa 409
 * (PERSONEL_PASAPORT_BELGESI_KIMLIK_DOGRULANMIS), tarih eksikse 400 — mesajı olduğu gibi gösterin.
 * `data` = GET /kimlik-dogrulama gövdesi.
 * PATCH /api/v1/personnel/{personId}/kimlik-dogrulama/pasaport-belgesi
 */
export async function setPasaportBelgesi(personId, { durum, gecerlilikTarihi = null, aciklama = '' } = {}) {
  try {
    const body = { durum: Number(durum) };
    if (gecerlilikTarihi) body.gecerlilikTarihi = String(gecerlilikTarihi).slice(0, 10);
    if (aciklama) body.aciklama = aciklama;
    const response = await axios.patch(`${BASE}/${personId}/kimlik-dogrulama/pasaport-belgesi`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişinin kimlik belgesini (file_type 90) yükler; `data` yeni `file_id`dir.
 * #394: `expiryDate` (yyyy-MM-dd, isteğe bağlı) belgenin GERÇEK son kullanma tarihidir (pasaport / kimlik
 * üzerindeki). Tip 90, Yabancı bir firmanın her çalışanı için zorunludur (#314); tarih gönderilmezse backend
 * artık bugün + 60 gün YAZMAZ: belgeyi tarihsiz saklar ve inceleme bekleyen (Beklemede) bırakır, tarihi onaylayan
 * FDS görevlisi girer (#295).
 * WAS (until #394): Son kullanma tarihi BİLEREK gönderilmiyor: belge hiçbir yerde zorunlu değil ve bir davranışı
 * (uygunluk, kart) belirlemiyor, yalnızca kimlik doğrulamanın dayanağı olarak seçiliyor. Backend
 * varsayılanı bugün + 60 gün; bu bir sorun değil.
 * ⚠️ Backend `documents{}` içine tip 90'ı YALNIZCA bir meslek kuralı istiyorsa koyar — yüklenen
 * dosya yeniden okunduğunda satırda görünmez; yüklemeden sonra GET /kimlik-dogrulama'nın `kimlikBelgesi`
 * alanını yeniden okuyun (#469), dönen id tek başına yetmez.
 * #387: `fuarId` belgenin etiketlendiği fuardır (`file.fuar_id`), form alanı `fuar_id` olarak gider; verilmezse backend
 * kullanıcının SEÇİLİ fuarını veritabanından okur, o da yoksa belge fuarsız (0) kalır.
 * WAS (until #387): `fuar_id` istekte değil oturumdan alınır (`X-fuar-id` yalnız yerel bypass'ta işe yarar).
 * POST /api/v1/personnel/dosyaekle/{personId}
 */
// WAS (until #394): export async function uploadKimlikBelgesi(personId, file, { fuarId = null } = {}) {
export async function uploadKimlikBelgesi(personId, file, { fuarId = null, expiryDate = null } = {}) {
  try {
    const form = new FormData();
    form.append('file_type_id', String(KIMLIK_FOTOGRAFI_FILE_TYPE_ID));
    form.append('file_given_name', 'Kimlik Fotoğrafı');
    if (expiryDate) form.append('file_expirydate', expiryDate);   // #394
    if (Number(fuarId) > 0) form.append('fuar_id', String(fuarId));   // #387
    form.append('file', file);
    const headers = { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' };
    // WAS (until #387): if (fuarId) headers['X-fuar-id'] = fuarId;
    const response = await axios.post(`${BASE}/dosyaekle/${personId}`, form, { headers });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * #700 (backend #699): kişinin kimlik / pasaport GÖRSELİNİ (file_type 90) kaldırır — Havuz yolunu (Taşı → Havuza al → sil) gerektirmez.
 * Kişinin etkin tüm tip 90 bağları tek işlemde kaldırılır (dosya satırı ve baytlar kalır); durum 3 ("alanda doğrulanacak") 0'a döner,
 * FDS personeli doğrulamanın dayandığı bir belge varsa 1'i de 0'a çevirir. Pasaport NUMARASI silinmez.
 * Firma doğrulanmış (durum 1) kimliğin belgesini kaldıramaz → 409; kaldırılacak belge yoksa 404 — mesajı olduğu gibi gösterin.
 * `data` = GET /kimlik-dogrulama gövdesi (taze durum).
 * DELETE /api/v1/personnel/{personId}/kimlik-belgesi
 */
export async function kimlikBelgesiniKaldir(personId) {
  try {
    const response = await axios.delete(`${BASE}/${personId}/kimlik-belgesi`, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

// ─── KVKK durumu ve Özel İzin (rehber §7) ────────────────────────────────────────────

/**
 * PATCH /api/v1/personnel/{personId}/kvkk-status — `{ person_kvkk_status_id: 0|1|2|3 }`.
 * ⚠️ Giriş kuralını yalnızca **3** karşılar. Başarı yalın zarf döner (`data: null`), bu
 * yüzden yeni değeri göstermek için kişiyi yeniden okuyun.
 * Staff kontrolü YOK — firma hesabı da kendisi hakkında "ıslak imza teslim aldık" yazabilir
 * (rehberdeki yetki açığı listesi). Bu yüzden firma tarafında BUTON YAPMIYORUZ.
 */
export async function setKvkkStatus(personId, statusId) {
  try {
    const response = await axios.patch(
      `${BASE}/${personId}/kvkk-status`,
      { person_kvkk_status_id: Number(statusId) },
      { headers: getAuthHeader() },
    );
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişiye giriş yasağı koyar / kaldırır (backlog #285, backend #286). YALNIZCA SAKA personeli (firma 403).
 * Yasak KİŞİ düzeyindedir (her fuar); koyarken `not` zorunlu (en çok 500), kaldırırken yok sayılır ve not silinir.
 * Yanıt zarfının `data`sı yeni `yasak` nesnesi: `{ yasakli, not, tarih, degistirenUserId, degistirenAdi }`.
 * Olmayan yasağı kaldırmak 200 "zaten yok" döner.
 * PATCH /api/v1/personnel/{personId}/yasak — `{ yasakli: true|false, not? }`
 */
export async function setPersonYasak(personId, { yasakli, not = '' } = {}) {
  try {
    const body = { yasakli: Boolean(yasakli) };
    if (yasakli) body.not = String(not).trim();
    const response = await axios.patch(`${BASE}/${personId}/yasak`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * GET /api/v1/personnel/{personId}/single-permit?fuarId=
 * ⚠️ `fuarId` ZORUNLU query param. İzin yoksa 200 + gövdede LİTERAL `null` döner —
 * `.typeId` okumadan önce null kontrolü yapın.
 */
export async function getSinglePermit(personId, fuarId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/single-permit`, {
      params: { fuarId: Number(fuarId) },
      headers: getAuthHeader(),
    });
    return response.data ?? null;
  } catch (error) {
    parseError(error);
  }
}

/** `iptalSebebi` (backend #280): bir Özel İznin neden kapandığı. */
export const OZEL_IZIN_IPTAL_SEBEBI = { ELLE: 1, BELGELER_TAMAMLANDI: 2 };

/**
 * GET /api/v1/personnel/{personId}/single-permit/gecmis?fuarId= — bu kişinin bu fuarda aldığı HER Özel İzin,
 * yeniden eskiye (backend #280). Aktif olan da listede (`aktif: true`), iptal edilenler `aktif: false` +
 * `iptalTarihi` / `iptalEden` (null = sistem) / `iptalEdenAdi` / `iptalSebebi` (1 elle kaldırıldı, 2 belgeler
 * tamamlandı → otomatik iptal) / `iptalSebebiAdi`. Veren: `givenBy` / `givenByAdi` / `givenAt` — 2026-08-25'ten
 * önce verilmiş 2.527 satırda null (o zaman kimse kaydetmiyordu): "bilinmiyor" diye gösterilir, boş bırakılmaz.
 * Hiç izin almamışsa boş dizi. Düz dizi döner, zarf YOK.
 * `GET /single-permit`in aksine bu uç eski bir backend'de (V66 öncesi) yok — çağıran hatayı yutup geçmişsiz
 * devam etmeli.
 */
export async function getSinglePermitGecmisi(personId, fuarId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/single-permit/gecmis`, {
      params: { fuarId: Number(fuarId) },
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * POST /api/v1/personnel/{personId}/single-permit — Özel İzin ver / (typeId 0 ile) geri al.
 * ⚠️ Bu, işçiyi KVKK ve kimlik doğrulama dışında HİÇBİR belge olmadan içeri sokan çağrıdır: firma üçlüsü
 * ve mesleğin belgeleri atlanır. #474'ten beri kimliği doğrulanmamış kişiye verilmez (409, bkz.
 * `isKimlikDogrulanmamisHatasi`); yeni izinde tip yalnızca 3 / 2 / 5 (1 ve 4 → 400); boş `aciklama` NULL saklanır,
 * etiket tipin adından gelir. `permit_type_id: 0` (geri al) her zaman serbest. `fuar_id` GÖVDEDE, oturumdan
 * alınmaz (izin adı konmuş bir fuar için verilir).
 * 🚨 Backend'de staff kontrolü YOK (rehber §2/§7'de Deniz'e bildirilmiş açık) — bu yüzden
 * bu fonksiyon yalnızca inceleme tarafı sayfalarından çağrılıyor, firma tarafında yok.
 */
export async function grantSinglePermit(personId, { fuarId, permitTypeId = 3, aciklama = '' } = {}) {
  try {
    const body = { fuar_id: Number(fuarId), permit_type_id: Number(permitTypeId) };
    if (aciklama) body.permit_type_string = aciklama;
    const response = await axios.post(`${BASE}/${personId}/single-permit`, body, { headers: getAuthHeader() });
    return unwrapEnvelope(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * İzni geri alır (`permit_type_id: 0`). İzin olmasa da 200 döner. Backend #280'den beri satırı SİLMEZ, iptal eder
 * (`iptalSebebi` 1, `iptalEden` = oturumdaki kullanıcı); izin `getSinglePermitGecmisi`de görünmeye devam eder.
 * Aynı kişiye sonradan yeniden izin verilirse yeni bir satır eklenir.
 */
export async function revokeSinglePermit(personId, fuarId) {
  return grantSinglePermit(personId, { fuarId, permitTypeId: 0 });
}

// ─── TCKN ile kişi bulma (backend boşluğu — istemci tarafı çözüm) ────────────────────

/**
 * Bir TC/pasaport numarasını `tcNoMasked` ile eşleştirmek için maske üretir.
 * Backend maskesi: İLK 2 + yıldızlar + SON 2 (`14615418426` → `14*******26`) — yıldız
 * sayısı uzunluk-4'tür, bu yüzden farklı uzunluktaki numaralar birbirine karışmaz.
 */
export function maskIdNumber(idNumber) {
  const raw = String(idNumber || '').trim();
  if (raw.length < 5) return raw;
  return `${raw.slice(0, 2)}${'*'.repeat(raw.length - 4)}${raw.slice(-2)}`;
}

/**
 * ⚠️ BACKEND BOŞLUĞU: TC numarasından kişi bulan bir uç YOK. `GET /personnel/firma/{id}`
 * yalnızca "ad soyad" üzerinde LIKE yapar. (Satırlar #270'ten beri tam numarayı `tcNo` olarak da
 * taşır ve eşleşme ona göre birebir olur; `tcNo` gelmeyen satırda — eski backend, taşeron devri —
 * aşağıdaki maske karşılaştırmasına düşülür.) Rehber bunu zaten "farklı, inceleme tarafı bir sayfa" olarak açık
 * bırakmış (FE-PersonnelController-guide.md, "Asked for but not built").
 *
 * Geçici çözüm: seçili firma+fuar kadrosunu sayfa sayfa okuyup, aranan numaranın
 * MASKESİNİ satırların `tcNoMasked` değeriyle karşılaştırıyoruz. Maskeleme kayıplı
 * olduğu için birden fazla eşleşme olabilir — bu yüzden tek kayıt değil, EŞLEŞEN
 * KAYITLARIN LİSTESİ dönüyoruz ve kararı kullanıcı veriyor.
 *
 * Backend `GET /personnel/search?idNumber=` eklediğinde burası tek bir çağrıya iner.
 */
export async function findPersonelByIdNumber(firmaId, fuarId, idNumber, { maxPages = 25, size = 100 } = {}) {
  const target = maskIdNumber(idNumber);
  const matches = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= maxPages) {
    // eslint-disable-next-line no-await-in-loop -- sayfa sayısı önceki cevaptan geliyor
    const data = await getPersonelRosterAdmin(firmaId, fuarId, { page, size });
    totalPages = data.totalPages || 1;
    // #293 (backend #270): satırlar tam numarayı `tcNo` olarak taşır → birebir eşleşir; yalnızca `tcNo` gelmediyse
    // (eski backend, taşeron devri) maskeyle — o durumda birden çok eşleşme olabilir.
    const raw = String(idNumber || '').trim();
    matches.push(...(data.records || []).filter((r) => (r.tcNo ? r.tcNo === raw : r.tcNoMasked === target)));
    page += 1;
  }

  return { matches, target, scannedPages: Math.min(page - 1, maxPages), truncated: totalPages > maxPages };
}
